import {
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
  MAX_VIDEO_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE_MB,
} from "@/lib/constants/photos";
import { ExpectedError } from "@/lib/errors";

export type ImageKitUploadFileType = "image" | "video";
type UploadFileSize = Pick<File, "name" | "size">;

export function getImageKitUploadChecks(fileType: ImageKitUploadFileType = "image") {
  return `"file.size" <= "${getMaxUploadFileSizeMb(fileType)}MB"`;
}

export function getMaxUploadFileSizeMb(fileType: ImageKitUploadFileType = "image") {
  return fileType === "video" ? MAX_VIDEO_FILE_SIZE_MB : MAX_FILE_SIZE_MB;
}

export function getMaxUploadFileSize(fileType: ImageKitUploadFileType = "image") {
  return fileType === "video" ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE;
}

export function getOversizedUploadError(file: UploadFileSize, fileType: ImageKitUploadFileType = "image") {
  const maxFileSize = getMaxUploadFileSize(fileType);

  if (file.size <= maxFileSize) {
    return null;
  }

  return new ExpectedError(
    `${file.name} is larger than ${getMaxUploadFileSizeMb(fileType)}MB and cannot be uploaded`
  );
}

export function getOversizedFilesToastMessage(files: UploadFileSize[], fileType: ImageKitUploadFileType = "image") {
  const maxSizeMb = getMaxUploadFileSizeMb(fileType);

  if (files.length === 1) {
    return `${files[0].name} is larger than ${maxSizeMb}MB and cannot be uploaded`;
  }

  return `${files.length} ${fileType}s were larger than ${maxSizeMb}MB and were skipped`;
}

export function toExpectedImageKitFileSizeError(error: unknown, fileType: ImageKitUploadFileType = "image") {
  // Defense in depth for bypasses or ImageKit-side check failures after the client pre-filter.
  if (!isImageKitFileSizeError(error)) {
    return null;
  }

  return new ExpectedError(
    `This ${fileType} is larger than ${getMaxUploadFileSizeMb(fileType)}MB and cannot be uploaded`,
    error
  );
}

export function isImageKitFileSizeError(error: unknown) {
  const { code, message, statusCode } = getUploadErrorFields(error);
  const errorCode = code.toLowerCase();
  const errorMessage = message.toLowerCase();

  if (statusCode === 413) {
    return true;
  }

  if (errorCode.includes("file_size") || errorCode.includes("size_limit")) {
    return true;
  }

  // ImageKit check failures are returned as plain objects; keep text matching narrow and documented.
  return (
    errorMessage.includes("file.size") ||
    errorMessage.includes("file size") ||
    errorMessage.includes("too large") ||
    errorMessage.includes("larger than") ||
    (errorMessage.includes("file") && errorMessage.includes("maximum") && errorMessage.includes("size")) ||
    (errorMessage.includes("file") && errorMessage.includes("size limit"))
  );
}

function getUploadErrorFields(error: unknown) {
  if (!error) {
    return { code: "", message: "", statusCode: undefined };
  }

  if (typeof error === "string") {
    return { code: "", message: error, statusCode: undefined };
  }

  if (error instanceof Error) {
    return { code: "", message: error.message, statusCode: undefined };
  }

  if (typeof error === "object") {
    const uploadError = error as {
      $ResponseMetadata?: { statusCode?: unknown };
      code?: unknown;
      error?: unknown;
      help?: unknown;
      message?: unknown;
      reason?: unknown;
      status?: unknown;
      statusCode?: unknown;
    };
    // ImageKit's JS SDK attaches upload response metadata here, including HTTP status.
    const statusCode = getStatusCode(
      uploadError.statusCode ?? uploadError.status ?? uploadError.$ResponseMetadata?.statusCode
    );

    // ImageKit upload errors expose `message` and often `help`; SDK metadata/code fields are not always enumerable.
    return {
      code: [uploadError.code, uploadError.reason, uploadError.error]
        .filter((value): value is string => typeof value === "string")
        .join(" "),
      message: [uploadError.message, uploadError.reason, uploadError.error, uploadError.help]
        .filter((value): value is string => typeof value === "string")
        .join(" "),
      statusCode,
    };
  }

  return { code: "", message: String(error), statusCode: undefined };
}

function getStatusCode(value: unknown) {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
}
